import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, Search, Tag, Folder, BookOpen, ArrowRight, X, RotateCcw } from 'lucide-react';
import { getPublishedPosts, getCategories, getTags, getRecentPosts, calculateReadingTime, BlogPost, BlogCategory, BlogTag } from '../lib/blog';
import { BlogLayout } from '../components/BlogLayout';

export function BlogPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [recentPosts, setRecentPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  
  const currentPage = parseInt(searchParams.get('page') || '1');
  const selectedCategory = searchParams.get('category') || '';
  const selectedTag = searchParams.get('tag') || '';

  // Update search term when URL changes
  useEffect(() => {
    const urlSearchTerm = searchParams.get('search') || '';
    setSearchTerm(urlSearchTerm);
  }, [searchParams]);

  // Reset all filters function
  function resetAllFilters() {
    setSearchParams({});
    setSearchTerm('');
  }

  // Check if any filters are active
  const hasActiveFilters = selectedCategory || selectedTag || searchParams.get('search');
  
  // Mobile optimization
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    loadBlogData();
  }, [currentPage, selectedCategory, selectedTag, searchParams]);

  async function loadBlogData() {
    setLoading(true);
    try {
      // Get search term from URL params
      const urlSearchTerm = searchParams.get('search') || '';
      
      // Load posts
      const postsData = await getPublishedPosts({
        page: currentPage,
        category: selectedCategory,
        tag: selectedTag,
        search: urlSearchTerm || searchTerm
      });
      
      setPosts(postsData.posts as unknown as BlogPost[]);
      setTotalPages(postsData.totalPages);

      // Load sidebar data
      const [categoriesData, tagsData, recentData] = await Promise.all([
        getCategories(),
        getTags(),
        getRecentPosts()
      ]);

      setCategories((categoriesData as unknown as BlogCategory[]) || []);
      setTags((tagsData as unknown as BlogTag[]) || []);
      setRecentPosts(recentData || []);
    } catch (error) {
      console.error('Error loading blog data:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    const newParams = new URLSearchParams();
    if (searchTerm.trim()) {
      newParams.set('search', searchTerm.trim());
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  }

  function handlePageChange(page: number) {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  // Mobile optimization variables removed

  return (
    <BlogLayout>
      <Helmet>
        <title>Blog - Rulevia</title>
      </Helmet>
      {/* Compact Hero Section */}
      <section className="relative py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Blog
              </span>
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Expert insights on ad compliance and performance optimization
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Blog Posts */}
            <div className="lg:col-span-3">
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400"></div>
                </div>
              ) : posts.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-20 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10"
                >
                  <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-300 text-xl mb-6">No blog posts found</p>
                  <button
                    onClick={() => {
                      setSearchParams({});
                      setSearchTerm('');
                    }}
                    className="text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    Clear filters and try again
                  </button>
                </motion.div>
              ) : (
                <div className="space-y-8">
                  {posts.map((post, index) => (
                    <motion.article
                      key={post.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ 
                        duration: prefersReducedMotion ? 0 : 0.6,
                        delay: prefersReducedMotion ? 0 : index * 0.1,
                        ease: [0.25, 0.1, 0.25, 1]
                      }}
                      viewport={{ once: true, margin: "-50px" }}
                      className="group bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 hover:border-purple-500/30 transition-all duration-300 overflow-hidden cursor-pointer"
                      onClick={() => navigate(`/blog/${post.slug}`)}
                    >
                      {post.featured_image && (
                        <div className="relative h-64 overflow-hidden">
                          <img
                            src={post.featured_image}
                            alt={post.title}
                            className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        </div>
                      )}
                      
                      <div className="p-6 lg:p-8">
                        <div className="flex flex-wrap gap-2 mb-4">
                          {post.categories?.map(category => (
                            <span
                              key={category.id}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30"
                            >
                              <Folder className="w-3 h-3 mr-1" />
                              {category.name}
                            </span>
                          ))}
                        </div>

                        <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 group-hover:text-purple-300 transition-colors">
                          {post.title}
                        </h2>

                        <div className="flex flex-wrap items-center text-sm text-gray-400 mb-4 gap-4">
                          <div className="flex items-center">
                            <img
                              src={post.author?.avatar || '/default-avatar.png'}
                              alt={post.author?.name}
                              className="w-6 h-6 rounded-full mr-2 border border-white/20"
                            />
                            <span>{post.author?.name}</span>
                          </div>
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1 text-purple-400" />
                            <span>{formatDate(post.published_at!)}</span>
                          </div>
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1 text-blue-400" />
                            <span>{post.reading_time || calculateReadingTime(post.content || '')} min read</span>
                          </div>
                        </div>

                        <p className="text-gray-300 mb-6 line-clamp-3">
                          {post.excerpt || post.content.substring(0, 200) + '...'}
                        </p>

                        <div className="inline-flex items-center text-purple-400 group-hover:text-purple-300 font-semibold transition-colors">
                          Read more
                          <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="mt-12 flex justify-center"
                >
                  <nav className="flex space-x-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                          page === currentPage
                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg shadow-purple-500/30'
                            : 'bg-white/10 text-gray-300 border border-white/20 hover:bg-white/20 hover:text-white backdrop-blur-sm'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </nav>
                </motion.div>
              )}
          </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Reset Filters Button */}
              {hasActiveFilters && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-4"
                >
                  <button
                    onClick={resetAllFilters}
                    className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-red-600/20 to-orange-600/20 hover:from-red-600/30 hover:to-orange-600/30 text-red-300 hover:text-red-200 rounded-lg transition-all duration-300 border border-red-500/30 hover:border-red-400/40 group"
                  >
                    <RotateCcw className="w-5 h-5 mr-2 transition-transform group-hover:rotate-180 duration-500" />
                    Reset All Filters
                  </button>
                </motion.div>
              )}
              {/* Search */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: hasActiveFilters ? 0.2 : 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6"
              >
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Search className="w-5 h-5 mr-2 text-purple-400" />
                  Search Blog
                </h3>
                <form onSubmit={handleSearch}>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search posts..."
                      className="w-full pl-10 pr-12 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-400 transition-colors"
                    />
                    <Search className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => {
                          const newParams = new URLSearchParams(searchParams);
                          newParams.delete('search');
                          newParams.set('page', '1');
                          setSearchParams(newParams);
                          setSearchTerm('');
                        }}
                        className="absolute right-3 top-3.5 w-5 h-5 text-gray-400 hover:text-white transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </form>
              </motion.div>

              {/* Categories */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: hasActiveFilters ? 0.3 : 0.2 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6"
              >
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Folder className="w-5 h-5 mr-2 text-purple-400" />
                  Categories
                </h3>
                <ul className="space-y-2">
                  <li>
                    <button
                      onClick={() => {
                        const newParams = new URLSearchParams(searchParams);
                        newParams.delete('category');
                        newParams.set('page', '1');
                        setSearchParams(newParams);
                      }}
                      className={`text-left w-full px-3 py-2 rounded-lg transition-all duration-300 ${
                        !selectedCategory
                          ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                          : 'text-gray-300 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      All Categories
                    </button>
                  </li>
                  {categories.map(category => (
                    <li key={category.id}>
                      <button
                        onClick={() => {
                          const newParams = new URLSearchParams(searchParams);
                          newParams.set('category', category.slug);
                          newParams.set('page', '1');
                          setSearchParams(newParams);
                        }}
                        className={`text-left w-full px-3 py-2 rounded-lg transition-all duration-300 ${
                          selectedCategory === category.slug
                            ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                            : 'text-gray-300 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        {category.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Tags */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: hasActiveFilters ? 0.4 : 0.3 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6"
              >
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Tag className="w-5 h-5 mr-2 text-blue-400" />
                  Popular Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tags.map(tag => (
                    <button
                      key={tag.id}
                      onClick={() => {
                        const newParams = new URLSearchParams(searchParams);
                        if (selectedTag === tag.slug) {
                          // If clicking the same tag, remove it
                          newParams.delete('tag');
                        } else {
                          // Otherwise set the new tag
                          newParams.set('tag', tag.slug);
                        }
                        newParams.set('page', '1');
                        setSearchParams(newParams);
                      }}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm transition-all duration-300 ${
                        selectedTag === tag.slug
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg shadow-purple-500/30'
                          : 'bg-white/10 text-gray-300 border border-white/20 hover:bg-white/20 hover:text-white'
                      }`}
                    >
                      <Tag className="w-3 h-3 mr-1" />
                      {tag.name}
                    </button>
                  ))}
                </div>
              </motion.div>

              {/* Recent Posts */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: hasActiveFilters ? 0.5 : 0.4 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6"
              >
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-blue-400" />
                  Recent Posts
                </h3>
                <ul className="space-y-3">
                  {recentPosts.map(post => (
                    <li key={post.id} className="border-b border-white/10 pb-3 last:border-0">
                      <a
                        href={`/blog/${post.slug}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/blog/${post.slug}`);
                        }}
                        className="text-gray-300 hover:text-purple-300 transition-colors text-sm font-medium"
                      >
                        {post.title}
                      </a>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(post.published_at)}
                      </p>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </BlogLayout>
  );
}